export const INCOMING_AUTHENTICATE_RESPONSE = 0;
export const INCOMING_SUBSCRIBE_RESPONSE = 1;
export const INCOMING_NOTIFICATION = 2;
export const INCOMING_ACCOUNT_UPDATE = 3;
export const INCOMING_ADS_RESPONSE = 4;
export const INCOMING_BOT_RESPONSE = 5;
export const INCOMING_GENAI_RESPONSE = 6;
export const INCOMING_FEED_ENTRY = 7;
export const INCOMING_EXCHANGE_TICKER = 8;
export const INCOMING_JETSTREAM_EVENT = 9;

export const OUTGOING_AUTHENTICATE = 0;
export const OUTGOING_SUBSCRIBE = 1;
export const OUTGOING_UNSUBSCRIBE = 2;
export const OUTGOING_ANALYTICS_EVENT = 3;
export const OUTGOING_ADS_REQUEST = 4;
export const OUTGOING_BOT_QUERY = 5;
export const OUTGOING_GENAI_QUERY = 6;

export interface IncomingMessage {
  kind: number;
  content: any;
}

export interface OutgoingMessage {
  kind: number;
  content: any;
}

export type MessageListener = (content: string) => void;
export type ConnectionListener = () => void;

export interface WebSocketHandlerOptions {
  uri?: string;
  debug?: boolean;
}

export class WebSocketHandler {
  uri: string;
  debug: boolean;
  socket: WebSocket | null;
  messageListeners: Map<number, MessageListener[]>;
  connectionListeners: ConnectionListener[];

  constructor(options: WebSocketHandlerOptions = {}) {
    this.uri = options.uri ?? "ws://dwayne.localhost:3000/ws";
    this.debug = options.debug ?? false;
    this.socket = null;
    this.messageListeners = new Map();
    this.connectionListeners = [];
  }

  isOpen() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) return true;
    return false;
  }

  attachSocketEventListeners() {
    if (this.socket) {
      this.socket.addEventListener("open", () => {
        if (this.debug) {
          console.log("WebSocket connection open");
        }
      });

      this.socket.addEventListener("close", () => {
        if (this.debug) {
          console.log("WebSocket closed");
        }

        setTimeout(() => {
          this.connect();
        }, 5000);
      });

      this.socket.addEventListener("error", (event) => {
        console.error("WebSocket error: ", event);
      });

      this.socket.addEventListener("message", (event) => {
        try {
          const message = JSON.parse(event.data) as IncomingMessage;
          const listeners = this.messageListeners.get(message.kind) ?? [];
          for (const listener of listeners) {
            listener(message.content);
          }
        } catch (err) {
          console.error("error parsing message", err);
        }
      });
    }
  }

  async connect() {
    this.socket = new WebSocket(this.uri);
    this.attachSocketEventListeners();
    for (const listener of this.connectionListeners) {
      listener();
    }
  }

  addConnectionListener(listener: ConnectionListener) {
    this.connectionListeners.push(listener);
  }

  removeConnectionListeners() {
    this.connectionListeners = [];
  }

  addMessageListener(kind: number, listener: MessageListener) {
    const listeners = this.messageListeners.get(kind) ?? [];
    listeners.push(listener);
    this.messageListeners.set(kind, listeners);
  }

  removeMessageListener(kind: number, listener: MessageListener) {
    const listeners = this.messageListeners.get(kind) ?? [];
    for (let i = 0; i < listeners.length; i++) {
      if (listeners[i] === listener) {
        listeners.splice(i, 1);
      }
    }
  }

  async send(message: OutgoingMessage) {
    if (!this.socket) {
      await this.connect();
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  async sendAuthenticate(accessToken: string) {
    await this.send({
      kind: OUTGOING_AUTHENTICATE,
      content: { accessToken },
    });
  }

  async sendSubscribe(kind: string) {
    await this.send({
      kind: OUTGOING_SUBSCRIBE,
      content: { kind },
    });
  }

  async sendUnsubscribe(kind: string) {
    await this.send({
      kind: OUTGOING_UNSUBSCRIBE,
      content: { kind },
    });
  }

  async sendBotQuery(content: string) {
    await this.send({
      kind: OUTGOING_BOT_QUERY,
      content: { content },
    });
  }
}

export interface IncomingMessageAuthenticateContent {
  accountId?: string;
  error?: string;
}

export interface IncomingMessageSubscribeContent {
  error?: string;
}

export interface IncomingAccountUpdateContent {
  creditBalance?: string;
  notificationCount?: number;
}

export const BOT_RESPONSE_KIND_FULL = 0;
export const BOT_RESPONSE_KIND_PARTIAL = 1;

export const BOT_RESPONSE_CONTENT_TYPE_TEXT = 0;
export const BOT_RESPONSE_CONTENT_TYPE_MARKDOWN = 1;
export const BOT_RESPONSE_CONTENT_TYPE_RENDERED_DWML = 2;

export interface BotResponseMessage {
  messageId: string;
  messageKind: number;
  contentType: number;
  content: string;
}

export interface BotResponseClientCommand {
  command: string;
  args?: string;
}

export interface IncomingMessageBotResponseContent {
  message?: BotResponseMessage;
  command?: BotResponseClientCommand;
}

export interface IncomingExchangeTickerContent {
  exchangeId: string;
  productId: string;
  price: string;
  open24h: string;
  low24h: string;
  high24h: string;
  side: string;
}
